import { useMemo } from 'react';
import { useQuery } from '@apollo/client';
import { ENGAGEMENT_CONTENT } from '../../services/apollo/queries/engagementQueries';

const DEFAULT_LIMIT = 12;

const serializeError = (apolloError) => {
  if (!apolloError) return null;
  const graphQLError = apolloError.graphQLErrors?.[0];
  const networkError = apolloError.networkError;
  return {
    message:
      graphQLError?.message ||
      apolloError.message ||
      networkError?.message ||
      'Failed to load engagement content',
    code:
      graphQLError?.extensions?.code ||
      (networkError ? 'NETWORK_ERROR' : 'UNKNOWN'),
  };
};

/**
 * @param {{ limit?: number }} [options]
 */
const useEngagementContent = ({ limit } = {}) => {
  const variables = useMemo(
    () => ({ input: { limit: limit || DEFAULT_LIMIT } }),
    [limit]
  );

  const { data, loading, error, networkStatus, refetch } = useQuery(
    ENGAGEMENT_CONTENT,
    {
      variables,
      fetchPolicy: 'cache-and-network',
      nextFetchPolicy: 'cache-first',
      notifyOnNetworkStatusChange: true,
    }
  );

  const items = data?.engagementContent?.items ?? [];
  const fetchedAt = data?.engagementContent?.fetchedAt ?? null;
  const playlistId = data?.engagementContent?.playlistId ?? null;
  const weekStartDate = data?.engagementContent?.weekStartDate ?? null;

  const hasItems = items.length > 0;
  const isInitialLoading = loading && !hasItems;
  const isRefreshing = loading && hasItems;
  const isRefetching = networkStatus === 4 && hasItems;

  return {
    items,
    loading: isInitialLoading,
    refreshing: isRefreshing || isRefetching,
    error: serializeError(error),
    fetchedAt,
    playlistId,
    weekStartDate,
    refetch,
  };
};

export default useEngagementContent;
